import { Link } from 'react-router-dom';
import { Movie } from '@/mocks/movies';

interface MovieSectionProps {
  title: string;
  subtitle?: string;
  movies: Movie[];
  viewAllLink?: string;
}

export default function MovieSection({ title, subtitle, movies: movieList, viewAllLink }: MovieSectionProps) {
  return (
    <section className="py-8 md:py-12">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">{title}</h2>
            {subtitle && (
              <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>
            )}
          </div>
          {viewAllLink && (
            <Link
              to={viewAllLink}
              className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {movieList.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </div>
    </section>
  );
}

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-2.5 bg-background-200">
        <img
          src={movie.poster}
          alt={movie.title}
          title={`${movie.title} - 4K电影高清画质在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        <div className="absolute top-2 left-2 flex items-center gap-1">
          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded whitespace-nowrap">
            {movie.quality}
          </span>
          {movie.isNew && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-accent-500 text-background-50 rounded whitespace-nowrap">
              NEW
            </span>
          )}
        </div>

        <div className="absolute top-2 right-2">
          <span className="flex items-center gap-0.5 text-xs font-medium text-background-50 bg-background-950/60 px-1.5 py-0.5 rounded">
            <i className="ri-star-fill text-accent-400 text-[10px]"></i>
            {movie.rating}
          </span>
        </div>

        {(movie.episodeTotal && movie.episodeCurrent && movie.episodeCurrent < movie.episodeTotal) && (
          <div className="absolute bottom-2 left-2">
            <span className="px-1.5 py-0.5 text-[10px] bg-background-950/70 text-primary-400 rounded whitespace-nowrap">
              更新至{movie.episodeCurrent}集
            </span>
          </div>
        )}

        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50">
            <i className="ri-play-fill text-xl"></i>
          </div>
        </div>
      </div>

      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
        {movie.title}
      </h3>
      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
        <span>{movie.year}</span>
        <span>|</span>
        <span className="line-clamp-1">{movie.genre.slice(0, 2).join(' · ')}</span>
      </div>
    </Link>
  );
}